<script setup lang="ts">
import { X } from 'lucide-vue-next';

defineOptions({ inheritAttrs: false });

const { title, dismissable = true } = defineProps<{
  title?: string;
  imageSrc?: string;
  dismissable?: boolean;
}>();

const emit = defineEmits(['close']);

const show = defineModel<boolean>('show', { required: true });
const modal = ref<HTMLDivElement>();

const close = () => {
  show.value = !dismissable;
  if (!show.value) emit('close');
};

// These two events were closing ALL the open modals (not just the active one)
// onClickOutside(modal, close, { ignore: ['.overlay'] });
// onKeyStroke('Escape', close);
</script>

<template>
  <Teleport to="body">
    <Transition
      name="nested"
      :duration="{
        enter: 200,
        leave: 200,
      }"
    >
      <div v-if="show" class="fixed inset-0 z-20 grid place-items-center bg-black/40 backdrop-blur-md">
        <div
          ref="modal"
          :class="$attrs.class"
          class="overlay max-h-[90vh] gap-2 rounded-xl bg-white p-4 shadow-[0_0_40px_rgba(0,0,0,0.2)] md:p-6"
          flex="~ col"
        >
          <div class="w-full flex justify-between">
            <div class="flex items-center gap-2">
              <img v-if="imageSrc" :src="imageSrc" size="8" class="self-end">
              <span v-if="title" class="text-xl font-medium">{{ title }}</span>
            </div>
            <SBButton v-if="dismissable" :lucide-icon="X" variant="ghost" @click="close()" />
          </div>
          <div class="overflow-y-auto p-1">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="css">
.nested-enter-active,
.nested-leave-active {
  transition: opacity 200ms ease-in-out;
}

.nested-enter-from,
.nested-leave-to {
  opacity: 0;
}

.nested-enter-active .overlay,
.nested-leave-active .overlay {
  transition: all 150ms ease-in-out;
}

.nested-enter-active .overlay,
.nested-leave-active .overlay {
  transition-delay: 50ms;
}

.nested-enter-from .overlay,
.nested-leave-to .overlay {
  transform: scale(0.9);
  opacity: 0;
}
</style>
